import "./VerImagenEstudioQr.css";
import { useState, useContext } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { IonSpinner } from "@ionic/react";
import { PortalContext } from "../../../context/Provider";
import { GlobalContext } from "../../../../global/context/Provider";
import { showToaster } from "../../../../global/context/action/toaster/toaster.js";
import axiosLogin from "../../../../global/helpers/axiosLogin";
import InputV1 from "../InputV1/InputV1";
import Temporizador from "../Temporizador/Temporizador";

/**
 * Modal del QR de informe (acción 23).
 * Pide SOLO la clave de 4 caracteres que se le entregó al paciente (en el email, o al
 * descargar/imprimir el informe). Con el token (de la URL del QR) + la clave, el backend
 * valida (token vigente + clave correcta) y devuelve la URL del visor de la imagen.
 * Si algún paso falla, se avisa y se cancela sin exponer datos sensibles.
 */
const VerImagenEstudioQr = ({ token, emailMasked, dissmiss }) => {
  const { modalDispatch } = useContext(PortalContext);
  const { toasterDispatch } = useContext(GlobalContext);

  const [clave, setClave] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [disableReenviar, setDisableReenviar] = useState(false);

  const continuar = async () => {
    const claveTrim = (clave || "").trim().toUpperCase();
    if (claveTrim.length !== 4) {
      setError("Ingresá el código de 4 caracteres.");
      return;
    }

    // Abrir la pestaña ANTES del await (gesto del usuario) para evitar el bloqueo de pop-ups.
    let win = null;
    if (!Capacitor.isNativePlatform()) {
      win = window.open("", "_blank");
    }

    setLoading(true);
    setError("");
    try {
      const isMobile = Capacitor.isNativePlatform() || window.innerWidth < 768;

      // El backend valida token + clave + vigencia y devuelve la URL del visor (anónimo).
      const api = await axiosLogin();
      const resp = await api.post("dis/informes/imagen-viewer-qr", {
        token,
        clave: claveTrim,
        isMobile,
      });
      const url = resp?.data?.url;
      if (!url) {
        throw new Error("SIN_IMAGEN");
      }

      if (win) {
        win.location.href = url;
      } else {
        await Browser.open({ url });
      }

      setLoading(false);
      dissmiss();
    } catch (e) {
      setLoading(false);
      if (win) win.close();
      // Mensaje al usuario SIN exponer datos sensibles. El backend manda un title acotado.
      const backendMsg = e?.response?.data?.title || e?.response?.data?.detail;
      const msg =
        e?.message === "SIN_IMAGEN"
          ? "No se encontró la imagen del estudio."
          : typeof backendMsg === "string" && backendMsg
            ? backendMsg
            : "No pudimos validar el código. Verificalo e intentá nuevamente.";
      setError(msg);
      showToaster({ texto: msg, tipo: "danger" }, "centroArriba")(toasterDispatch);
    }
  };

  const reenviar = async () => {
    // Cooldown de 60s (Temporizador) apenas se toca, para evitar spam.
    setDisableReenviar(true);
    try {
      const api = await axiosLogin();
      await api.post("dis/informes/reenviar-codigo-qr", { token });
      showToaster(
        {
          texto: "Te reenviamos el código a tu correo.",
          tipo: "success",
        },
        "centroArriba",
      )(toasterDispatch);
    } catch (e) {
      const backendMsg = e?.response?.data?.title;
      showToaster(
        {
          texto:
            typeof backendMsg === "string" && backendMsg
              ? backendMsg
              : "No pudimos reenviar el código. Probá nuevamente en un momento.",
          tipo: "danger",
        },
        "centroArriba",
      )(toasterDispatch);
    }
  };

  return (
    <div className="verImagenQr">
      <div className="verImagenQr-header bgcG-latex30">
        <h1 className="verImagenQr-title rb18b c-white">Imagen del estudio</h1>
        <div className="cruz" onClick={dissmiss}>
          <div className="cruzParte1"></div>
          <div className="cruzParte2"></div>
        </div>
      </div>
      <div className="verImagenQr-body">
        <p className="verImagenQr-text rb16t c-latex10">
          Ingresá el código que recibiste por correo electrónico
          {emailMasked && (
            <>
              {" "}
              a <span className="rb16m">{emailMasked}</span>
            </>
          )}{" "}
          para acceder a las imágenes de tu estudio
        </p>

        <div className="verImagenQr-field">
          <InputV1
            name="clave_qr"
            headerStr="Código"
            placeholderText="A2C5"
            disabledEjemplo={true}
            inputType="text"
            value={clave}
            onChange={(e) => {
              setClave((e.target.value || "").toUpperCase());
              if (error) setError("");
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !loading) {
                continuar();
              }
            }}
            className="inputNonScroll rb16m"
            maxLength="4"
            isRequired={true}
          />
        </div>

        {error && <p className="verImagenQr-error rb16t">{error}</p>}

        <div className="verImagenQr-actions">
          <button
            className="verImagenQr-btn verImagenQr-btn--ghost rb16m"
            onClick={reenviar}
            disabled={disableReenviar || loading}
          >
            {disableReenviar ? (
              <Temporizador onChange={() => setDisableReenviar(false)} />
            ) : (
              "Reenviar código"
            )}
          </button>
          <button
            className="verImagenQr-btn verImagenQr-btn--primary rb16m"
            onClick={continuar}
            disabled={loading}
          >
            {loading ? <IonSpinner name="lines-small" /> : "Continuar"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerImagenEstudioQr;
